import firebase from 'firebase/app';
import * as Util from '@/util.ts';
import * as Enums from '@/enums.ts';
import './register-service-worker.ts';
import 'normalize.css';

declare global {
  interface String {
    supplant(o: string[]): string;
  }
  interface Date {
    dstOffsetForNorthernHemisphere(): number;
  }
  interface ResourceObj {
    docId: string;
  }
  interface ClientUser {
    displayName: string;
    photoURL: string;
    email: string;
    deliveryAddress: DeliveryAddress | null;
    highestConfirmedAge: number;
    settings: UserSettings;
  }
  interface ClientUserUpdate {
    displayName?: string;
    photoURL?: string;
    email?: string;
    deliveryAddress?: DeliveryAddress | null;
    highestConfirmedAge?: number;
    settings?: UserSettings;
  }
  interface UserSettings {
    locale?: TranslationLocaleCode;
    contactEmail: string; // If they want their exposed email to be something different.
    contactNumber: number | null;
    referenceName: string;
    includeTaxWithPrices: boolean;
    showImagesAutomatically: boolean;
  }
  interface Subscription {
    docId?: string;
    ownerId: string;
    isActive: boolean;
    stripeAccount: StripeAccount | null;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  interface StripeAccount {
    stripeUserId: string;
    refreshToken: string;
    livemode: boolean;
    scope: string;
  }
  interface Establishment {
    docId: string;
    name: string;
    menus: Menu[]; // List of menu / time slot tuples.
    description: string;
    primaryColor: number[];
    accentColor: number[];
    fontColorOnPrimary: number[];
    fontColorOnAccent: number[];
    primaryFont: string;
    primaryFontVariant: string;
    primaryFontScale: number;
    secondaryFont: string;
    secondaryFontVariant: string;
    secondaryFontScale: number;
    secondaryFontUppercase: boolean;
    showPwaPromptImmediately: boolean;
    priceDisplayFormat: Enums.ePriceDisplayFormats;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
    images: Image[];
  }
  interface GoogleAddress {
    administrativeAreaLevel1: string;
    administrativeAreaLevel2: string;
    country: string;
    locality: string;
    postalCode: string;
    route: string;
    streetNumber: string;
  }
  interface EstablishmentLocation {
    docId: string;
    email: string;
    phoneNumber: string;
    formattedAddress: string;
    address: GoogleAddress;
    latitude: number | null;
    longitude: number | null;
    googlePlaceId: string;
    utcOffset: number;
    numberOfStations: number;
    establishment: Establishment | null; // An array of length 1. Making this an array keeps the functionality for document reference conversion consistent.
    locationId: number;
    jsonFeedEnabled?: boolean;
    promotion: Promotion | null;
    tablesideTimeSlots: TimeSlot[];
    takeoutTimeSlots: TimeSlot[];
    deliveryTimeSlots: TimeSlot[];
    isTablesideOn: boolean;
    isTakeoutOn: boolean;
    isDeliveryOn: boolean;
    orderAheadEnabled: boolean;
    sendEmailsForOpenHours: boolean;
    maximumDeliveryOrderDistance: number; // km
    minimumDeliveryOrderAmount: number; // cents.
    deliveryChargeAmount: number;
    closureDates: string[];
    showLocationWarning: boolean;
    enableOrderPrePayingForTableside: boolean;
    enableOrderWithoutPayingForTakeout: boolean;
    enableOrderWithoutPayingForDelivery: boolean;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  // TODO: Index these.
  interface EstablishmentPermalink {
    docId: string; // Should be the permalink itself.
    subscriptionDoc: firebase.firestore.DocumentReference; // subscriptions/0PSn8M85UQfDA33EvVsLnmHa8RB2/locationCollection
    establishmentDoc: firebase.firestore.DocumentReference;
  }
  interface Promotion {
    docId: string;
    name: string;
    timeSlots: TimeSlot[];
    showOnSessionStart: boolean;
    images: Image[];
  }
  interface Menu {
    docId: string;
    name: string;
    timeSlots: TimeSlot[];
    isVisible: boolean;
    categories: MenuCategory[];
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
    images: Image[];
  }
  interface MenuItem {
    docId: string;
    timeSlots: TimeSlot[];
    isVisible: boolean;
    isSoldOut: boolean;
    name: string;
    secondaryName: string;
    description: string | null;
    price: number;
    minimumQuantity: number;
    displayPrice: number;
    presetOptionGroups: OptionGroup[];
    customOptionGroups: OptionGroup[];
    combinedOptionGroups: OptionGroup[];
    temperature: Enums.eTemperatures | null;
    caloriesExplicit: number | null;
    expectedWaitTime: number | null; // In minutes
    isBeverage: boolean;
    isHealthy: boolean | null;
    isAlcoholic: boolean | null;
    isVegetarian: boolean | null;
    isGlutenFree: boolean | null;
    isHalal: boolean | null;
    isKosher: boolean | null;
    isDairyFree: boolean | null;
    isPeanutFree: boolean | null;
    isSpicy: boolean | null;
    serves: number | null;
    preciseSizeValue: number;
    preciseSizeUnit: Enums.eMeasurementUnits;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
    images: Image[];
  }
  interface MenuCategory {
    docId: string;
    timeSlots: TimeSlot[];
    isVisible: boolean;
    name: string;
    description: string;
    price: number; // Used in AYCE kind of scenarios. When something is ordered from this category this price is applied
    menuItems: MenuItem[];
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
    images: Image[];
  }
  interface OptionGroup {
    docId: string;
    name: string;
    isVisible: boolean;
    options: Option[];
    numberOfSelections: number;
    mustPickExactAmount: boolean;
    canSelectMultipleOfSameOption: boolean;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  interface TimeSlot {
    docId: string;
    name: string;
    timeOfDayStart: string;
    timeOfDayEnd: string;
    daysOfWeek: string[];
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  interface Option {
    name: string;
    priceChange: number;
    quantity: number;
    isSoldOut: boolean;
  }
  interface Image {
    url: string;
    refPath: string;
    name: string;
    fileToUpload?: File | null;
  }
  interface NameVal {
    name: string;
    val: number;
  }
  interface OrderItem {
    paymentIntentId: string;
    orderItemId: string;
    notificationId?: string; // The id of the notification associated with this this order item.
    billId?: string;
    quantity: number;
    menuItemDocId: string;
    menuItemName: string;
    basePrice: number;
    priceWithModifiers: number; // base price + modifiers.
    totalPrice: number; // (base price + modifiers) * quantity.
    totalPriceWithTax: number;
    specialRequest?: string;
    selectedOptions?: SelectedOptions; // Format is an object key'd on group name with a value of the selected options for that group.
    cancelRequestPending?: boolean;
    wasCancelled?: boolean;
    dateCreated: number;
  }
  interface SelectedOptions {
    [key: string]: Option[];
  }
  interface Bill {
    billId: string;
    notificationId?: string;
    total: number;
    orderItems?: OrderItem[];
    categoryCharges?: CategoryCharge[];
    splitOrderItems?: SplitOrderItems; // Since nested arrays are not supported. This is an object that contains keys of the form split1, split2, split3 etc, and the values are OrderItem[].
    splitCategoryCharges?: SplitCategoryCharges; // Since nested arrays are not supported. This is an object that contains keys of the form split1, split2, split3 etc, and the values are OrderItem[].
  }
  interface CategoryCharge {
    categoryId: string;
    name: string;
    quantity: number;
    totalPrice: number;
    totalPriceWithTax: number;
  }
  interface SplitOrderItems {
    [key: string]: OrderItem[];
  }
  interface SplitCategoryCharges {
    [key: string]: CategoryCharge[];
  }
  interface FilterObject {
    showIndeterminate: boolean;
    selectedAttributes: any;
    highestValues: any;
    lowestValues: any;
    ranges: any;
  }
  interface SimpleLocation {
    email: string;
    address: string;
    phoneNumber: number;
  }
  interface UserPaymentRecord {
    paymentIntentId: string;
    stripeUserId: string;
    establishmentName: string;
    locationDoc: firebase.firestore.DocumentReference; // A doc reference as a string. Probably should have been an actual doc reference object but oh well.
    location: SimpleLocation;
    currency: string;
    chargeStatus: Enums.eChargeStatuses;
    GenericOrderObject: GenericOrderObject;
    isLive: boolean;
    dateCreated: firebase.firestore.FieldValue;
  }
  interface GlobalPaymentRecord extends UserPaymentRecord {
    userId: string;
    referenceName: string;
  }
  interface TMNotification {
    notificationId: string;
    status: Enums.eNotificationStatuses;
    type: Enums.eNotificationTypes;
    userId?: string;
    linkId?: string;
    distanceFromRequester?: number | null;
    response?: string;
    expectedWait?: number;
    metadata?: ExchangeObjectMetaData;
    addressedById?: string;
    addressedByDisplayName?: string;
    dateCreated?: number;
  }
  interface OrderNotification extends TMNotification {
    orderItems: OrderItem[];
  }
  interface CancellationRequestNotification extends TMNotification {
    orderItems: OrderItem[];
    cancellationRequestOrderItemId?: string;
  }
  interface BillNotification extends TMNotification {
    bill: Bill;
  }
  interface MessageNotification extends TMNotification {
    message: string;
  }
  interface GenericOrderObjectNotification extends TMNotification {
    orderInstanceId: string;
    paymentIntentId: string; // Only used when verifying that a notification should exist.
    orderItems: OrderItem[];

    // The following only exists if this was a paid for order.
    amount?: number;
    tip?: number;
    generalRequests?: string;
  }
  interface PaidTakeoutOrderNotification extends GenericOrderObjectNotification {
    pickUpCode: string;
    requestedTime: number; // Minutes from now
  }
  interface PaidDeliveryOrderNotification extends GenericOrderObjectNotification {
    deliveryAddress: DeliveryAddress;
    deliveryChargeAmount: number;
    requestedTime: number; // Minutes from now
  }
  interface AnyNotification extends TMNotification {
    cancellationRequestOrderItemId?: string;
    orderItems?: OrderItem[];
    bill?: Bill;
    message?: string;
    amount?: number;
    tip?: number;
    generalRequests?: string;
    requestedTime?: number;
    deliveryAddress?: DeliveryAddress;
    pickUpCode?: string;
    orderInstanceId?: string;
    paymentIntentId?: string; // Only used when verifying that a notification should exist.
  }
  interface ExchangeObjectMetaData {
    userEstablishmentExchangeDoc?: firebase.firestore.DocumentReference;
    userEstablishmentExchangeObject?: UserEstablishmentExchangeObject;
  }
  interface DeliveryAddress {
    formattedAddress: string;
    address: GoogleAddress;
    addressLine2: string | null;
    additionalDetails: string;
    latitude: number | null;
    longitude: number | null;
    googlePlaceId: string;
    utcOffset: string;
  }
  interface GenericOrderObject {
    orderInstanceId: string;
    type: Enums.eOrderTypes;
    orderItems: OrderItem[];
    status: Enums.eOrderStatuses;
    generalRequests: string;
    dateCreated: number;
    expectedWait?: number;
    rejectionReason?: string;
    notYetVerified?: boolean;
    preTaxAmountForOrder: number;
    postTaxAmountForOrder: number;
    amount: number; // Total amount. Post tax amount + tip amount + post tax delivery charge.

    // The following only exists if this was a paid for order.
    tip?: number;
    tipAmount?: number;
    preTaxDeliveryChargeAmount?: number;
    postTaxDeliveryChargeAmount?: number;
    paymentIntentId: string; // Must be the id of the payment intent.
  }
  interface TakeoutOrderObject extends GenericOrderObject {
    type: Enums.eOrderTypes.takeout;
    requestedTime: number; // Minutes from now
    pickUpCode: string;
  }
  interface DeliveryOrderObject extends GenericOrderObject {
    type: Enums.eOrderTypes.delivery;
    requestedTime: number; // Minutes from now
    deliveryAddress: DeliveryAddress;
    deliveryChargeAmount: number;
  }
  interface AnyGenericOrderObject extends GenericOrderObject {
    requestedTime?: number; // Minutes from now
    pickUpCode?: string;
    deliveryAddress?: DeliveryAddress;
    deliveryChargeAmount?: number;
  }
  interface VerifiedStripeEvent {
    paymentIntentId: string;
    amount: number;
  }
  interface UserEstablishmentExchangeObject {
    docId: string;
    stationId: number;
    takeOutStationId: number;
    deliveryStationId: number;
    tablesideStationId: number;
    verifiedAuthorizations: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    verifiedPayments: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    verifiedCancellations: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    deliveryOrderCollection: { [orderInstanceId: string]: DeliveryOrderObject };
    takeoutOrderCollection: { [orderInstanceId: string]: TakeoutOrderObject };
    tablesideOrderCollection: { [orderInstanceId: string]: GenericOrderObject };
    pendingBills: Bill[];
    acceptedBills: Bill[];
    rejectedBills: Bill[];
    cancelledBills: Bill[];
    liked: string[];
    disliked: string[];
    notificationsToTheEstablishment: TMNotification[];
    notificationsToTheUser: TMNotification[];
    userNotificationToken: string;
    pushNotificationMessage?: PushNotificationMessage | null;
    referenceName: string;
    contactEmail: string;
    contactNumber: number | null;
    serverReview: number | null;
    experienceReview: number | null;
    isFirstTimeAtLocation: boolean;
    tablesideViewedCounts: Count;
    takeoutViewedCounts: Count;
    deliveryViewedCounts: Count;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  interface Count {
    [key: string]: number;
  }
  interface UserEstablishmentExchangeObjectUpdate {
    docId?: string;
    stationId?: number;
    takeOutStationId?: number;
    deliveryStationId?: number;
    tablesideStationId?: number;
    // verifiedAuthorizations?: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    // verifiedPayments?: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    // verifiedCancellations?: { [paymentIntentId: string]: VerifiedStripeEvent }; // Can only be updated server side.
    deliveryOrderCollection?: { [orderInstanceId: string]: DeliveryOrderObject };
    takeoutOrderCollection?: { [orderInstanceId: string]: TakeoutOrderObject };
    tablesideOrderCollection?: { [orderInstanceId: string]: GenericOrderObject };
    pendingBills?: Bill[];
    acceptedBills?: Bill[];
    rejectedBills?: Bill[];
    cancelledBills?: Bill[];
    liked?: string[];
    disliked?: string[];
    notificationsToTheEstablishment?: TMNotification[];
    notificationsToTheUser?: TMNotification[];
    userNotificationToken?: string;
    pushNotificationMessage?: PushNotificationMessage | null;
    referenceName?: string;
    contactEmail?: string;
    contactNumber?: number | null;
    serverReview?: number | null;
    experienceReview?: number | null;
    isFirstTimeAtLocation?: boolean;
    tablesideViewedCounts?: Count;
    takeoutViewedCounts?: Count;
    deliveryViewedCounts?: Count;
    dateCreated?: firebase.firestore.FieldValue;
    dateUpdated?: firebase.firestore.FieldValue;
  }
  type TranslationLocaleCode = 'de' | 'es' | 'fr' | 'zh' | 'en';
}
interface PushNotificationMessage {
  destination: Enums.ePushDesinations;
  title: string;
}

// Prototype changes. Written by Douglas Crockford.
String.prototype.supplant = function supplant(o: string[]): string {
  return this.replace(/{([^{}]*)}/g,
    (a, b) => {
      const r = o[b];
      return typeof r === 'string' || typeof r === 'number' ? r : a;
    }
  );
};

Date.prototype.dstOffsetForNorthernHemisphere = function() {
  const jan = new Date(this.getFullYear(), 0, 1);
  const jul = new Date(this.getFullYear(), 6, 1);
  return jul.getTimezoneOffset() - jan.getTimezoneOffset();
};

Util.initApp();
